import { ADD_TO_GPT_SANDBOX, GPT_REDO, GPT_UNDO, TICKET_DETAILS_LOADING, toggleSnackbar, UPDATE_GPT_SANDBOX_CONTENT } from '../../redux/actions/authActions';
import { Box, Button, ButtonGroup, debounce, IconButton, MenuItem, Select, Skeleton, Slider, Stack, Tooltip, Typography } from '@mui/material';
import { generateGPTAnswer, generateOneTouchAnswer, insertTextToTicket, updateEvent } from '../../api';
import GeneratingTokensOutlinedIcon from '@mui/icons-material/GeneratingTokensOutlined';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ReactQuillEditor from '../../components/ReactQuillEditor';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { convertMarkdownToHtml } from '../../constants/utils';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

const SandboxSkeleton = () => {
    return (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", width: "100%", height: "60vh", gap: 5 }}>
            {/* Top Controls */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", height: "10%" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Skeleton variant="rectangular" width={120} height={36} />
                    <Skeleton variant="rectangular" width={150} height={36} />
                </Box>
                <ButtonGroup variant="contained" color="inherit" aria-label="Undo Redo Operations">
                    <Button startIcon={<UndoIcon />} disabled>Undo</Button>
                    <Button endIcon={<RedoIcon />} disabled>Redo</Button>
                </ButtonGroup>
            </Box>
            {/* Main Editor Area */}
            <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "80%", gap: 10 }}>
                {/* Simulate Editor */}
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
                {/* Slider + Transformation Options */}
                <div style={{ display: "flex", flexDirection: "row", width: "100%", padding: "0 1rem", justifyContent: 'space-between', alignItems: "center" }}>
                    {/* Slider */}
                    <div style={{ height: "inherit", display: "flex", alignItems: "center", width: "50%" }}>
                        <Box sx={{ width: 150 }}>
                            <Skeleton variant="rectangular" width="100%" height={10} />
                            <Skeleton width={80} height={20} sx={{ mt: 1 }} />
                        </Box>
                    </div>
                    {/* Icon Buttons */}
                    <Stack direction="row" spacing={2} sx={{ width: "50%", justifyContent: "space-around", alignItems: "center" }}>
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} variant="circular" width={36} height={36} />
                        ))}
                    </Stack>
                </div>
            </div>
            {/* Bottom Buttons */}
            <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", gap: 10, height: "10%" }}>
                <Skeleton variant="rectangular" width={100} height={36} />
                <Skeleton variant="rectangular" width={100} height={36} />
            </div>
        </div>
    );
};

const GPTSandbox = ({ isTicketIdValid, agentDetails, userEmail, dialogViewSandbox = false }) => {
    const [source, setSource] = useState("Articles");
    const orgDetails = useSelector((state) => state.auth.orgDetails);
    const prismToken = useSelector((state) => state.auth.prismToken);
    const ticketDetails = useSelector((state) => state.ticket.ticketDetails);
    const ticketingSystem = orgDetails?.ticketing?.platform;
    const ticketDataloading = useSelector((state) => state.ticket.ticketDetailsLoading);
    const sandBoxStack = useSelector((state) => state.sandbox.sandBoxStack);
    const sandBoxLoading = useSelector((state) => state.sandbox.gptSandboxLoading)
    const gptAnswerForTickets = useSelector((state) => state.ticket.gptAnswerForTickets);
    const gptAnswerForArticles = useSelector((state) => state.ticket.gptAnswerForArticles);
    const gptAnswerForMacros = useSelector((state) => state.ticket.gptAnswerForMacros);
    const currentSandBoxIndex = useSelector((state) => state.sandbox.sandBoxStack.gptSandBoxCurrentContentIndex);
    const [intensity, setIntensity] = useState(3);
    const [undoTitle, setUndoTitle] = useState("Undo")
    const [redoTitle, setRedoTitle] = useState("Redo")
    const [sandBoxContent, setSandboxContent] = useState(sandBoxStack.gptSandBox[currentSandBoxIndex].content);
    const textRef = useRef(null);
    const [selectedText, setSelectedText] = useState('');
    const [beforeText, setBeforeText] = useState('');
    const [afterText, setAfterText] = useState('');
    const dispatch = useDispatch();
    const intensityValues = [
        {
            value: 1,
            label: 'Mild',
        },
        {
            value: 2,
            label: 'Moderate',
        },
        {
            value: 3,
            label: 'Balance',
        },
        {
            value: 4,
            label: 'High',
        },
        {
            value: 5,
            label: 'Maximum',
        }
    ];

    function valuetext(event) {
        setIntensity(event.target.value)

    }

    function valueLabelFormat() {
        const text = intensityValues.find(i => i.value === intensity);
        return text.label

    }

    const handleMouseUp = useCallback(() => {
        const editor = textRef?.current?.getEditor();
        const range = editor?.getSelection();

        if (range && range.length > 0) {
            const fullDelta = editor.getContents();

            const beforeDelta = editor.getContents(0, range.index);
            const selectedDelta = editor.getContents(range.index, range.length);
            const afterDelta = editor.getContents(range.index + range.length, fullDelta.length() - (range.index + range.length));

            const beforeHTML = new QuillDeltaToHtmlConverter(beforeDelta.ops, {}).convert();
            const selectedHTML = new QuillDeltaToHtmlConverter(selectedDelta.ops, {}).convert();
            const afterHTML = new QuillDeltaToHtmlConverter(afterDelta.ops, {}).convert();

            setBeforeText(beforeHTML);
            setSelectedText(selectedHTML);
            setAfterText(afterHTML);
        } else {
            clearSelection();
        }
    }, [textRef.current]);

    useEffect(() => {
        const editor = textRef?.current?.getEditor();
        editor?.root?.addEventListener('mouseup', handleMouseUp);
        return () => {
            editor?.root?.removeEventListener('mouseup', handleMouseUp);
        };
    }, [textRef.current, handleMouseUp]);

    const clearSelection = () => {
        setSelectedText('');
        setBeforeText('');
        setAfterText('');
    };

    const handleButtonClick = () => {
        const buttonAnswerMap = {
            "Articles": gptAnswerForArticles,
            "Tickets": gptAnswerForTickets,
            "Macros": gptAnswerForMacros
        }

        if (buttonAnswerMap[source].length) {
            dispatch({
                type: ADD_TO_GPT_SANDBOX,
                payload: {
                    action: `GPT ${source}`, content: convertMarkdownToHtml(buttonAnswerMap[source].replaceAll('\n', '<br>'))
                }
            })
        }
        else {
            dispatch(generateOneTouchAnswer(`GPT ${source}`, orgDetails?.subdomain_name, ticketingSystem, ticketDetails, userEmail, agentDetails?.id, source, orgDetails?._id, prismToken, dispatch));
        }
    };

    const dispatchRef = useRef(dispatch);

    const debouncedDispatchUpdate = useCallback(
        debounce((index, content) => {
            dispatchRef.current({
                type: UPDATE_GPT_SANDBOX_CONTENT,
                payload: { index, content }
            });
        }, 500),
        []
    );

    const updateSandboxContent = (value) => {
        setSandboxContent(value);
        if (textRef.current) {
            const quill = textRef.current.getEditor();
            if (quill?.getText()?.length > 1) {
                debouncedDispatchUpdate(currentSandBoxIndex, value); // debounced update
            }
        }

    }

    function renderCurrentSandboxState() {
        const currentContentIndex = currentSandBoxIndex
        const updateButtonState = (buttonId, index, stack, isUndo) => {
            if (isUndo) {
                const title = currentContentIndex <= 0 ? 'Undo' : `Undo ${sandBoxStack['gptSandBox'][currentContentIndex]['action']}`
                setUndoTitle(title)
            }
            else {
                const title = currentContentIndex < sandBoxStack['gptSandBox'].length - 1 ? `Redo ${sandBoxStack['gptSandBox'][currentContentIndex + 1]['action']}` : 'Redo'
                setRedoTitle(title)
            }
        };

        updateButtonState("undoButton", sandBoxStack.gptSandBoxCurrentContentIndex, sandBoxStack.gptSandBox, true);
        updateButtonState("redoButton", sandBoxStack.gptSandBoxCurrentContentIndex, sandBoxStack.gptSandBox, false);
    }

    function undoSandboxOperation() {
        const currentContentIndex = sandBoxStack[`gptSandBoxCurrentContentIndex`]
        if (currentContentIndex > 0) {
            dispatch({ type: GPT_UNDO })
        }
    }

    function redoSandboxOperation() {
        const currentContentIndex = sandBoxStack[`gptSandBoxCurrentContentIndex`]
        if (currentContentIndex < sandBoxStack['gptSandBox'].length - 1) {
            dispatch({ type: GPT_REDO })
        }
    }

    const handleGPTGenerate = (actionType) => {
        if (sandBoxContent?.length > 0) {
            dispatch(generateGPTAnswer(actionType, sandBoxContent.replaceAll("<br>", "\n"), ticketDetails, userEmail, agentDetails?.role, intensity, prismToken, orgDetails?._id, selectedText, beforeText, afterText));
        } else {
            dispatch(toggleSnackbar({ message: "Unable To Perform GPT Operation: GTP Sandbox Is Empty", status: true, severity: "error" }));
        }
    }

    const handleClearContent = (actionType) => {
        if (sandBoxContent?.length) {
            dispatch({
                type: ADD_TO_GPT_SANDBOX,
                payload: {
                    action: actionType, content: ''
                }
            })
        }
        dispatch(updateEvent("gptClear", "", ticketDetails?.id, ticketDetails?.subject, userEmail, orgDetails?._id, prismToken))
    }

    const handleComment = () => {
        const content = selectedText || sandBoxContent;
        if (!content.trim()) {
            dispatch(toggleSnackbar({ message: "Unable To Insert The Content Into Ticket: GTP Sandbox Is Empty", status: true, severity: "error" }));
        } else {
            dispatch({ type: TICKET_DETAILS_LOADING, payload: true })
            dispatch(insertTextToTicket(content, ticketingSystem, orgDetails?.subdomain_name, userEmail, orgDetails?._id, ticketDetails, prismToken))
        }
    }

    useEffect(() => {
        setSandboxContent(sandBoxStack['gptSandBox'][currentSandBoxIndex]['content'])
        renderCurrentSandboxState();
        clearSelection()
    }, [sandBoxStack['gptSandBox'], currentSandBoxIndex])

    return (
        <div>
            {
                isTicketIdValid ?
                    sandBoxLoading ?
                        <SandboxSkeleton /> :
                        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", width: "100%", height: "60vh", gap: 5 }}>

                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", height: "10%" }}>
                                {!dialogViewSandbox &&
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Select value={source} size="small" onChange={(event) => setSource(event.target.value)}>
                                            <MenuItem value={"Articles"}>Articles</MenuItem>
                                            <MenuItem value={"Tickets"}>Tickets</MenuItem>
                                            <MenuItem value={"Macros"}>Macros</MenuItem>
                                        </Select>
                                        <Button onClick={handleButtonClick} variant="outlined" color="inherit" endIcon={<GeneratingTokensOutlinedIcon />} sx={{ textTransform: "none", fontWeight: "bold" }} >
                                            Answer {source}
                                        </Button>
                                    </Box>}
                                <ButtonGroup variant="contained" color="inherit" aria-label="Undo Redo Operations">
                                    <Tooltip title={undoTitle} placement="bottom" arrow>
                                        <Button startIcon={<UndoIcon />} onClick={undoSandboxOperation} >Undo</Button>
                                    </Tooltip>
                                    <Tooltip title={redoTitle} placement="bottom" arrow>
                                        <Button endIcon={<RedoIcon />} onClick={redoSandboxOperation} >Redo</Button>
                                    </Tooltip>
                                </ButtonGroup>
                            </Box>
                            <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "80%", justifyContent: "space-between" }}>
                                <ReactQuillEditor content={sandBoxContent} onChange={updateSandboxContent} ref={textRef} />
                                <div style={{ display: "flex", flexDirection: "row", width: "100%", padding: "0 1rem", justifyContent: 'space-between', alignItems: "center" }}>
                                    <div style={{ height: "inherit", display: "flex", alignItems: "center", width: "50%" }}>
                                        <Box sx={{ width: 150, height: "auto" }}>
                                            <Slider aria-label="Always visible" defaultValue={3} onChange={valuetext} value={intensity} step={1} min={1} max={5} size="medium" valueLabelDisplay="off" />
                                            <Typography id="non-linear-slider" mt={-1} fontSize={"14px"}>
                                                <strong >Intensity:</strong> {valueLabelFormat(intensity)}
                                            </Typography>
                                        </Box>
                                    </div>
                                    <Stack sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center", justifyContent: "space-around", width: "50%" }}>
                                        {[
                                            { label: "Make this succinct", icon: "🤏" },
                                            { label: "Clean this up", icon: "🧹" },
                                            { label: "Make this more empathetic", icon: "📝" },
                                            { label: "Make this friendlier", icon: "🧑" },
                                        ].map(({ label, icon }) => (
                                            <Tooltip key={label} title={label} placement="bottom" arrow>
                                                <IconButton color="inherit" onClick={() => handleGPTGenerate(label)} sx={{ fontSize: 20, transition: "transform 0.2s ease", "&:hover": { transform: "scale(1.2)", }, }}>
                                                    {icon}
                                                </IconButton>
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                </div>
                            </div>
                            <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", gap: 10, height: "10%" }}>
                                <Tooltip title="Clear Sandbox Content" placement="bottom" arrow>
                                    <Button disabled={ticketDataloading} onClick={() => handleClearContent('clear')} color="inherit" variant="contained" startIcon={<ClearOutlinedIcon />}>clear</Button>
                                </Tooltip>
                                <Tooltip title="Insert the sandbox content" placement="bottom" arrow>
                                    <Button disabled={ticketDataloading} loading={ticketDataloading} loadingPosition="end" onClick={() => handleComment('comment')} color="inherit" variant="contained" startIcon={<AddCommentOutlinedIcon />} >Insert</Button>
                                </Tooltip>
                            </div>
                        </div>
                    :
                    <div></div>
            }
        </div>
    )
}

export default GPTSandbox